import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Hourglass } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PinDialog from "./PinDialog";
import { Button } from "./ui/button";

export default function ScreenTimeGuard() {
  const { activeKid, isKid } = useAuth();
  const [usage, setUsage] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!isKid) return;
    try { const { data } = await api.get(`/profiles/${activeKid.profile_id}/usage`); setUsage(data); } catch (e) { console.error("usage fetch failed", e); }
  }, [isKid, activeKid]);

  const tick = useCallback(async () => {
    if (!isKid) return;
    try { const { data } = await api.post(`/profiles/${activeKid.profile_id}/usage`, { minutes: 1 }); setUsage(data); } catch (e) { console.error("usage tick failed", e); }
  }, [isKid, activeKid]);

  useEffect(() => {
    if (!isKid) return;
    load();
    timer.current = setInterval(tick, 60000);
    return () => clearInterval(timer.current);
  }, [isKid, load, tick]);

  if (!isKid || !usage) return null;
  const locked = usage.remaining <= 0;
  const low = usage.remaining <= 10;

  const unlock = async () => {
    try { const { data } = await api.post(`/profiles/${activeKid.profile_id}/reset-usage`); setUsage(data); } catch (e) { console.error("usage reset failed", e); }
    setPinOpen(false);
  };

  return (
    <>
      <span data-testid="screen-time-remaining"
        className={`hidden sm:flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${low ? "bg-rose-100 text-rose-600" : "bg-sky-100 text-sky-700"}`}>
        <Clock className="h-4 w-4" /> {usage.remaining}m
      </span>

      <AnimatePresence>
        {locked && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[115] bg-gradient-to-br from-[#2AB7CA] to-[#2EC4B6] flex items-center justify-center p-6"
            data-testid="screen-time-lock">
            <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
              <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
                <Hourglass className="h-16 w-16 text-[#2AB7CA] mx-auto" />
              </motion.div>
              <h2 className="text-3xl font-display font-bold text-slate-900 mt-4">Time's up for today! 🌙</h2>
              <p className="text-slate-500 mt-2">
                Great job{activeKid?.name ? `, ${activeKid.name}` : ""}! You've used all {usage.limit} minutes of Bubz time.
                Come back tomorrow for more fun.
              </p>
              <Button data-testid="unlock-screen-btn" onClick={() => setPinOpen(true)}
                className="mt-6 rounded-full bg-[#2EC4B6] hover:bg-[#26a69a] font-bold px-6 h-12">
                Grown-up: add more time
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PinDialog open={pinOpen} onClose={() => setPinOpen(false)} onSuccess={unlock}
        title="Add more screen time" subtitle="Enter the parent PIN to reset today's timer." />
    </>
  );
}
